import { FormParams, getParamsFromForm } from "@helpers/utils";
import replacePersonalCansSection from "./personalised-cans/lib/replacePersonalCansSection";

import productQuery from "../graphql/queries/product.gql";

const fallbackCartData = {
  attributes: {},
  cart_level_discount_applications: [],
  currency: "",
  item_count: 0,
  items: [],
  items_subtotal_price: "",
  note: null,
  original_total_price: 0,
  requires_shipping: false,
  token: null,
  total_discount: 0,
  total_price: 0,
  total_weight: 0,
};

export interface CartItem {
  url: string;
  handle: string;
  product_title: string;
  id: string;
  product_id: string;
  quantity: number;
  properties: Record<string, string | string[] | Record<string, string>>;
  title: string;
  image: string;
  original_price: string;
  original_line_price: string;
  line_price: string;
  price: string;
  sku: string;
  product_has_only_default_variant: boolean;
  options_with_values: Record<string, string>[];
  variant_id: string;
  inventory_quantity: number;
}

export interface CartData {
  attributes: object;
  cart_level_discount_applications: string[];
  currency: string;
  item_count: number;
  items: CartItem[];
  items_subtotal_price: string;
  note: string | null;
  original_total_price: number;
  requires_shipping: boolean;
  token: string | null;
  total_discount: number;
  total_price: number;
  total_weight: number;
}

export interface ICart extends HTMLElement {
  cartData: CartData;
  fetchCartData(): Promise<CartData>;
  handleAtcSubmit(event: Event): Promise<void>;
  addItem(
    id: string,
    quantity?: number,
    properties?: Record<string, string | string[] | Record<string, string>>,
    selling_plan?: string | string[] | Record<string, string>,
  ): void;
  addMultipleItems(
    items: {
      id: string;
      quantity: number;
      properties: Record<string, string | string[] | Record<string, string>>;
      selling_plan: string | string[] | Record<string, string>;
    }[],
  ): void;
  addNote(note: string): void;
  addAttribute(key: string, value: string): void;
  addMultipleAttributes(attributes: Record<string, string>): void;
  changeItemQuantity(itemId: string | number, quantity: number): void;
  changeItemProperties(
    itemId: string | number,
    properties: Record<string, string>,
  ): void;
  getCartData(): CartData;
  sendAddToCart(
    items: {
      id: string;
      quantity: number;
      properties: Record<string, string | string[] | Record<string, string>>;
      selling_plan: string | string[] | Record<string, string>;
    }[],
  ): Promise<void>;
}

export class Cart extends HTMLElement implements ICart {
  static get observedAttributes() {
    return ["cart-data"];
  }

  private _cartData!: CartData;

  constructor() {
    super();
    const initialCartData = this.getAttribute("cart-data") as string | null;
    const parsedInitialCartData: CartData = initialCartData
      ? JSON.parse(initialCartData)
      : null;

    if (parsedInitialCartData && parsedInitialCartData.item_count) {
      this.cartData = parsedInitialCartData;
    } else {
      this.cartData = fallbackCartData;
    }
  }

  connectedCallback() {
    this.fetchCartData();

    const atcForms = document.querySelectorAll("form[data-productForm='true']");
    const cartData: CartData = JSON.parse(
      this.getAttribute("cart-data") || "{}",
    );

    atcForms.forEach((form) => {
      if (
        form
          .querySelector(".product-swatches__group")
          ?.classList.contains("product-swatches__single")
      ) {
        form.addEventListener("submit", this.handleAtcSubmit.bind(this));
      } else {
        form.addEventListener(
          "submit",
          this.handleMultipleAtcSubmit.bind(this),
        );
      }
    });

    const quantityInputs: NodeListOf<HTMLInputElement> =
      document.querySelectorAll(".product-swatches__quantity input");
    for (const input of quantityInputs) {
      const inputVariantId = input.dataset.id;
      const inputVariantInCart = cartData.items.find(
        (item) => item.id.toString() === inputVariantId,
      );
      if (inputVariantInCart) {
        input.max = (
          parseInt(input.max) - inputVariantInCart.quantity
        ).toString();
      }
    }
  }

  attributeChangedCallback(name: string, newValue: string) {
    if (name === "cart-data") {
      this._cartData = JSON.parse(newValue);
    }
  }

  get cartData(): CartData {
    return this._cartData;
  }

  set cartData(data: CartData) {
    this._cartData = data;
    this.updateAttribute(data);
  }

  public getCartData(): CartData {
    return this._cartData;
  }

  private updateAttribute(data: CartData): void {
    this.setAttribute("cart-data", JSON.stringify(data));
  }

  private updateOnCartChange() {
    this.setAttribute("cart-data", JSON.stringify(this.cartData));
  }

  public async fetchCartData(fireEvent = false): Promise<CartData> {
    const cartDataResponse = await fetch(`${window.themeVars.routes.cart}.js`);
    this.cartData = await cartDataResponse.json();

    this.updateOnCartChange();

    if (!fireEvent) {
      return this.cartData;
    }

    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: this.cartData }),
    );

    return this.cartData;
  }

  public async handleMultipleAtcSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const cartItems = await this.fetchCart();

    const itemsToAdd: {
      id: string;
      quantity: number;
      properties: Record<string, string | string[] | Record<string, string>>;
      selling_plan: string | string[] | Record<string, string>;
    }[] = [];
    const variants = document.querySelectorAll(".product-swatches__form-group");
    variants.forEach((variant) => {
      const bundleProductData = variant.getAttribute("data-bundle-product");
      let bundleItem;
      if (
        bundleProductData &&
        bundleProductData !== "null" &&
        bundleProductData !== null
      ) {
        bundleItem = JSON.parse(bundleProductData);
      }
      const quantityInput = variant.querySelector(
        ".product-swatches__quantity input",
      ) as HTMLInputElement;
      const quantity = parseInt(quantityInput?.value);
      let quantityToAdd = quantity;
      const bundleID = bundleItem?.variants[0]?.id;
      const quantityMax = quantityInput?.dataset.inventoryQuantity
        ? parseInt(quantityInput?.dataset.inventoryQuantity)
        : null;
      const variantId = quantityInput.dataset.id;
      const pfandValue = quantityInput?.getAttribute("data-pfand");

      cartItems.items.forEach((item) => {
        if (
          (variantId && item.id.toString() === variantId) ||
          item.properties["_variant_id"] === variantId
        ) {
          if (quantityMax !== null && quantity > 0) {
            quantityToAdd = quantityMax - item.quantity;
          }
        }
      });

      const properties: { [key: string]: string } = pfandValue
        ? { Pfand: pfandValue }
        : {};

      if (bundleID) {
        // Finds the variant id of the bundle item from the product object by SKU
        properties["_variant_id"] = variantId || "";
        itemsToAdd.push({
          id: bundleID,
          quantity: quantityToAdd,
          properties: properties,
          selling_plan: "",
        });
      } else {
        itemsToAdd.push({
          id: variantId as string,
          quantity: quantityToAdd,
          properties: properties,
          selling_plan: "",
        });
      }

      quantityInput.value = "0";
      const newMaxQuantity = Number(quantityInput.max) - quantityToAdd;
      quantityInput.max = newMaxQuantity.toString();
    });

    if (itemsToAdd.length > 0) {
      this.addMultipleItems(itemsToAdd);
    }
    const hasQuantity = itemsToAdd.some((item) => item.quantity > 0);

    if (!hasQuantity) {
      this.displayNoQuantity();
    }
  }

  private displayNoQuantity() {
    const productSwatches = document.querySelector("uwp-product-swatches");

    if (!productSwatches) {
      return;
    }

    productSwatches.classList.add("product-swatches--no-quantity");
    productSwatches.scrollIntoView({ behavior: "smooth" });
  }

  public async handleAtcSubmit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const params = getParamsFromForm(form);

    if (!params.id) {
      return console.error("handleAtcSubmit: No variant ID found in form.");
    }

    const quantity = parseInt(params.quantity as string, 10) || 1;
    const selling_plan = params.selling_plan || "";
    const properties = Cart.extractProperties(params);

    this.addItem(params.id.toString(), quantity, properties, selling_plan);
  }

  private static extractProperties(
    params: FormParams,
  ): Record<string, string | string[] | Record<string, string>> {
    const propertyInputKeys = Object.keys(params).filter((key) =>
      key.startsWith("properties"),
    );
    const properties: Record<
      string,
      string | string[] | Record<string, string>
    > = {};
    propertyInputKeys.forEach((key) => {
      const propertyName = key.replace("properties[", "").replace("]", "");
      properties[propertyName] = params[key];
    });
    return properties;
  }

  public addItem(
    id: string,
    quantity = 1,
    properties: Record<string, string | string[] | Record<string, string>> = {},
    selling_plan: string | string[] | Record<string, string> = "",
  ): void {
    if (!id) {
      return console.error("Cart.addItem() : id is required.");
    }

    const item = {
      id,
      quantity,
      properties,
      selling_plan,
    };

    this.sendAddToCart([item]);
    return;
  }

  public addMultipleItems(
    items: {
      id: string;
      quantity: number;
      properties: Record<string, string | string[] | Record<string, string>>;
      selling_plan: string | string[] | Record<string, string>;
    }[],
  ): void {
    if (!items || !items.length) {
      return console.error("Cart.addMultipleItems() : items are required.");
    }

    const hasQuantity = items.some((item) => item.quantity > 0);

    if (!hasQuantity) {
      return console.error(
        "Cart.addMultipleItems() : at least one item must have a quantity.",
      );
    }

    this.sendAddToCart(items);
  }

  public async fetchCart(): Promise<CartData> {
    const response = await fetch("/cart.js");
    return await response.json();
  }

  public async sendAddToCart(
    items: {
      id: string;
      quantity: number;
      properties: Record<string, string | string[] | Record<string, string>>;
      selling_plan: string | string[] | Record<string, string>;
    }[],
  ): Promise<void> {

    let itemsWithPreOrder = items;
    
    if ((window as any).ThemeVars?.product?.preOrder === 'true') {

      const preOrderDateField = `${(window as any).ThemeVars?.product?.preOrderDispatchEta}` ? `${(window as any).ThemeVars?.product?.preOrderDispatchEta}` : '';

      function formatDispatchDate(isoDate: string): string {
        if (!isoDate) return "";
        const parts = isoDate.split("-").map((p) => parseInt(p, 10));
        if (parts.length !== 3) return isoDate;
        const [year, month, day] = parts;
        if (isNaN(year) || isNaN(month) || isNaN(day)) return isoDate;
      
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
      
        const getOrdinal = (d: number): string => {
          const v = d % 100;
          if (v >= 11 && v <= 13) return "th";
          switch (d % 10) {
            case 1:
              return "st";
            case 2:
              return "nd";
            case 3:
              return "rd";
            default:
              return "th";
          }
        };
      
        const monthName = monthNames[Math.max(0, Math.min(11, month - 1))];
        return `${day}${getOrdinal(day)} ${monthName} ${year}`;
      }

      const formattedPreOrderDispatchDate =
        typeof preOrderDateField === "string"
          ? formatDispatchDate(preOrderDateField)
          : "";

      const preOrderShipDate = window.themeVars.strings.cart.preOrderShipDate.replace(
        "{{ date }}",
        formattedPreOrderDispatchDate,
      )

      itemsWithPreOrder = items.map((item) => ({
        ...item,
        properties: {
          ...(item.properties || {}),
          '_pre_order': 'true',
          'Estimated Dispatch Date': preOrderShipDate,
        },
      }));
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ items: itemsWithPreOrder }),
    });

    const cart = await response.json();

    console.log(
      cart,
      "cart.sendAddToCart()",
      `${(window as any).ThemeVars?.product?.preOrder ?? ""}`,
    )

    if (cart.error || cart.status) {
      console.error(
        `Cart.sendAddToCart() : ${cart.description || cart.message || "Unknown error"}`,
      );
    } else {
      // Fetch the latest cart data
      const updatedCartData = await this.fetchCartData();
      replacePersonalCansSection(updatedCartData);

      // Dispatch an event with the updated cart data
      document.dispatchEvent(
        new CustomEvent("cart:added", { detail: updatedCartData }),
      );

      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: updatedCartData }),
      );
    }
  }

  public addNote(note: string): void {
    const updateBody = {
      note: "", // Clear the standard cart note
      attributes: {
        message: note,
      },
    };
    this.sendCartUpdate(updateBody);
  }

  public addAttribute(key: string, value: string): void {
    const updateBody = { attributes: { [key]: value } };
    this.sendCartUpdate(updateBody);
  }

  public addMultipleAttributes(attributes: Record<string, string>): void {
    const updateBody = { attributes };
    this.sendCartUpdate(updateBody);
  }

  private async sendCartUpdate(
    updateBody: Record<string, Record<string, string> | string>,
  ): Promise<void> {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(updateBody),
    });
    const cart = await response.json();
    if (cart.status) {
      console.error(
        `Cart.sendCartUpdate() : ${cart.description || cart.message || "Unknown error"}`,
      );
    } else {
      this.cartData = cart;
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
      this.updateOnCartChange();
    }
  }

  public changeItemQuantity(itemId: string | number, quantity: number): void {
    const itemKey = parseInt(itemId.toString(), 10) < 1000 ? "line" : "id";
    const changeBody = {
      [itemKey]: itemId.toString(),
      quantity,
    };
    this.sendCartChange(changeBody);
  }

  public changeItemProperties(
    itemId: string | number,
    properties: Record<string, string>,
  ): void {
    if (!properties || Object.keys(properties).length === 0) {
      return console.error(
        "Cart.changeItemProperties() : properties are required and cannot be empty.",
      );
    }
    const itemKey =
      typeof itemId === "string" || parseInt(itemId.toString(), 10) < 1000
        ? "line"
        : "id";
    const changeBody = {
      [itemKey]: itemId.toString(),
      properties,
    };
    this.sendCartChange(changeBody);
  }

  private async sendCartChange(
    changeBody: Record<string, Record<string, string> | string | number>,
  ): Promise<void> {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(changeBody),
    });

    if (response.status && response.status !== 200) {
      console.error(
        `Cart.sendCartChange() : ${response.status === 404 ? "Item not in cart" : "Unknown error"}`,
      );
    } else {
      // Fetch the latest cart data
      const updatedCartData = await this.fetchCartData();
      replacePersonalCansSection(updatedCartData);

      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: updatedCartData }),
      );
    }
  }
}

customElements.define("uwp-cart", Cart);
